import { create } from "zustand";
import { userApi, type MySearchKeywordsResponse } from "~/api/user";
import * as Sentry from "@sentry/react";

export interface MySearchState {
  // Data
  hotKeywords: {
    keywords: string[];
    timestamp: number;
  } | null;
  recommendedKeywords: string[];

  // State
  isLoading: boolean;
  error: string | null;
  isLoaded: boolean;

  // Actions
  fetchMySearchKeywords: () => Promise<void>;
  reset: () => void;

  // Atomic operation to check and set loading state
  trySetLoading: () => boolean;
  setNotLoading: () => void;
}

export const useMySearchStore = create<MySearchState>((set, get) => ({
  // Data
  hotKeywords: null,
  recommendedKeywords: [],

  // State
  isLoading: false,
  error: null,
  isLoaded: false,

  // Atomic check-and-set operation for loading state
  trySetLoading: () => {
    const state = get();
    if (state.isLoading) {
      return false; // Already loading, operation rejected
    }
    set({ isLoading: true, error: null });
    return true; // Successfully set to loading
  },

  setNotLoading: () => {
    set({ isLoading: false });
  },

  fetchMySearchKeywords: async () => {
    // Use atomic check-and-set
    if (!get().trySetLoading()) {
      return;
    }

    try {
      const response: MySearchKeywordsResponse = await userApi.getMySearchKeywords();

      set({
        hotKeywords: response.hotKeywords,
        recommendedKeywords: response.recommendedKeywords,
        isLoaded: true,
      });
    } catch (error) {
      Sentry.captureException(error);
      set({
        error: error instanceof Error ? error.message : "Failed to fetch search keywords",
      });
    } finally {
      get().setNotLoading();
    }
  },

  reset: () => {
    set({
      hotKeywords: null,
      recommendedKeywords: [],
      isLoading: false,
      error: null,
      isLoaded: false,
    });
  },
}));
